/**
 * "Send portal invite" / "Send password reset email" sidebar card, shared
 * between OwnerForm.tsx, TenantForm.tsx, and VendorForm.tsx. Unlike
 * `ConnectCard` (Stripe-specific, owner/vendor-only), this is identical
 * across all three apart from the resource path, capability, and the "no
 * portal account yet" copy, so it's one component rather than duplicated
 * per form.
 *
 * The two actions are mutually exclusive by construction, not by a shared
 * disabled state: `hasPortalAccount` picks which one even applies (there is
 * no password to reset before an account exists, and no invite to (re)send
 * once one does), so only one button ever renders.
 *
 * The one addition to that: for tenants with a login, ADMIN/DEVELOPER also
 * get `ManualPasswordReset` (`tenants:reset-password`) beneath the reset-email
 * button. It's a different action, not an alternative to the email.
 *
 * Same busy/sent/error local-state pattern as `ConnectCard` — no
 * polling/refetch needed, since both actions only fire an email.
 *
 * For `resource: 'vendors'` specifically, the "no portal account yet"
 * button does double duty — see
 * `sendVendorPortalAndConnectInvite`'s own doc comment
 * (modules/vendor-disbursements/connect-service.ts) — so its copy reads
 * "Send enrollment invite" rather than "Send portal invite", even though it
 * hits the identical `/vendors/:id/portal-invite` route shape.
 */

import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useCan } from '../lib/capabilities.js';
import { ManualPasswordReset } from './ManualPasswordReset.js';

export function PortalAccessCard({
  resource,
  id,
  hasPortalAccount,
  email,
}: {
  resource: 'owners' | 'tenants' | 'vendors';
  id: string;
  hasPortalAccount: boolean;
  email: string;
}) {
  const canManage = useCan(
    resource === 'owners'
      ? 'owners:write'
      : resource === 'vendors'
        ? 'maintenance:write'
        : 'tenants:write',
  );
  // ADMIN/DEVELOPER only. Separate from canManage: a property manager can
  // send the reset email but can't see a generated password.
  const canResetManually = useCan('tenants:reset-password') && resource === 'tenants';
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function trigger(path: string, failureMessage: string) {
    setBusy(true);
    setError(null);
    try {
      await api(`/${resource}/${id}/${path}`, { method: 'POST' });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : failureMessage);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Portal access</h2>
      {hasPortalAccount ? (
        <>
          <span className="pill">Has login</span>
          {error ? <p className="alert alert--error">{error}</p> : null}
          {canManage ? (
            <button
              type="button"
              className="button button--quiet"
              disabled={busy || sent}
              onClick={() =>
                void trigger('reset-password-email', 'Could not send the password reset email.')
              }
            >
              {sent ? 'Reset email sent' : busy ? 'Sending…' : 'Send password reset email'}
            </button>
          ) : null}
          {canResetManually ? <ManualPasswordReset tenantId={id} /> : null}
        </>
      ) : (
        <>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            No login yet — an invite goes to {email}.
          </span>
          {error ? <p className="alert alert--error">{error}</p> : null}
          {canManage ? (
            <button
              type="button"
              className="button button--quiet"
              disabled={busy || sent}
              onClick={() => void trigger('portal-invite', 'Could not send the invite.')}
            >
              {sent
                ? 'Invite sent'
                : busy
                  ? 'Sending…'
                  : resource === 'vendors'
                    ? 'Send enrollment invite'
                    : 'Send portal invite'}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
